import { useState, Fragment } from 'react';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Grid,
    Divider,
    Box,
    Typography,
    Card,
    CardContent,
    useTheme,
} from '@mui/material';

import {
    Warning as WarningIcon,
    Flight as FlightIcon,
    Hotel as HotelIcon,
    DirectionsCar as CarIcon,
    Person as PersonIcon,
    Place as PlaceIcon,
    LocalOffer as CategoryIcon,
    Event as EventIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon
} from '@mui/icons-material';
import { Badge } from '../../design-system';
import { describeDescriptionEdit } from './DescriptionCell.js';
import descriptionStyles from './DescriptionCell.module.css';

import { MerchantLocationMap, TripRouteMap } from './LocationMapComponents.js';
import ViewStatementDialog from "./dialog-view-statement-pdf";
import { formatDate, formatCurrency } from '../../utils/formatters.js'

import MerchantViewCard from './MerchantViewCard.js';
import CheckImageLightbox from './CheckImageLightbox.js';

const TransactionDetailDialog = ({ open, selectedTransaction, handleClose }) => {

    const theme = useTheme();

    const [openViewStatementDialog, setOpenViewStatementDialog] = useState(false);
    const [openCheckImage, setOpenCheckImage] = useState(false);
    // handler for statement PDF preview
    const handleStatementPreview = () => {
        setOpenViewStatementDialog(true);
    };

    const handleStatementPreviewClose = () => {
        setOpenViewStatementDialog(false);
    }


    return (
        <Fragment>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                aria-labelledby="transaction-detail-dialog"
            >
                {selectedTransaction && (
                    <>
                        <DialogTitle id="transaction-detail-dialog">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6">
                                    Transaction Details
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PdfIcon />}
                                        onClick={() => handleStatementPreview()}
                                        size="small"
                                        sx={{ mr: 1 }}
                                    >
                                        Review Statement
                                    </Button>
                                    {selectedTransaction.flagName && (
                                        <Chip
                                            icon={<WarningIcon />}
                                            label={selectedTransaction.flagName}
                                            color="error"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={3}>
                                {/* Basic Transaction Info */}
                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Date
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {formatDate(selectedTransaction.transactionDate)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Amount
                                                    </Typography>
                                                    <Typography variant="body1" sx={{
                                                        color: selectedTransaction.amount < 0 ? theme.palette.error.main : theme.palette.success.main,
                                                        fontWeight: 'bold',
                                                        fontSize: '1.2rem'
                                                    }}>
                                                        {formatCurrency(selectedTransaction.amount)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Description
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {selectedTransaction.description}
                                                    </Typography>
                                                    {selectedTransaction.descriptionEdited && (
                                                        <p className={descriptionStyles.editNote}>
                                                            <Badge variant="info">Edited</Badge>
                                                            <span>{describeDescriptionEdit(selectedTransaction)}</span>
                                                        </p>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Account Information */}
                                <Grid item xs={12} md={selectedTransaction.hasMerchantInfo() ? 6 : 12}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Account Information
                                            </Typography>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Account
                                            </Typography>
                                            <Typography variant="body1" gutterBottom>
                                                {selectedTransaction.bankAccountName}
                                            </Typography>

                                            <Typography variant="subtitle2" color="text.secondary">
                                                Account Holder
                                            </Typography>
                                            <Typography variant="body1" gutterBottom>
                                                {selectedTransaction.accountHolder}
                                            </Typography>

                                            <Typography variant="subtitle2" color="text.secondary">
                                                Bank
                                            </Typography>
                                            <Typography variant="body1">
                                                {selectedTransaction.bankName} ({selectedTransaction.accountType})
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Merchant Information - Only show if merchant info exists */}
                                {selectedTransaction.hasMerchantInfo() && (
                                    <MerchantViewCard merchant={selectedTransaction} />
                                )}

                                {/* Check / deposit-slip details — shown when this transaction was
                                    folded from a reproduced check or deposit-slip image */}
                                {selectedTransaction.hasCheckInfo() && (
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    {selectedTransaction.docKind === 'deposit_slip' ? 'Deposit Slip Details' : 'Check Details'}
                                                </Typography>
                                                {selectedTransaction.checkNumber && (
                                                    <>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Check Number
                                                        </Typography>
                                                        <Typography variant="body1" gutterBottom>
                                                            {selectedTransaction.checkNumber}
                                                        </Typography>
                                                    </>
                                                )}
                                                {selectedTransaction.checkPayee && (
                                                    <>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            {selectedTransaction.docKind === 'deposit_slip' ? 'Payer' : 'Payee'}
                                                        </Typography>
                                                        <Typography variant="body1" gutterBottom>
                                                            {selectedTransaction.checkPayee}
                                                        </Typography>
                                                    </>
                                                )}
                                                {selectedTransaction.checkPayeeAddress && (
                                                    <>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            {selectedTransaction.docKind === 'deposit_slip' ? 'Payer Address' : 'Payee Address'}
                                                        </Typography>
                                                        <Typography variant="body1" gutterBottom>
                                                            {selectedTransaction.checkPayeeAddress}
                                                        </Typography>
                                                    </>
                                                )}
                                                {selectedTransaction.checkMemo && (
                                                    <>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Memo
                                                        </Typography>
                                                        <Typography variant="body1" gutterBottom>
                                                            {selectedTransaction.checkMemo}
                                                        </Typography>
                                                    </>
                                                )}
                                                {selectedTransaction.hasCheckImage && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<ImageIcon />}
                                                        onClick={() => setOpenCheckImage(true)}
                                                        sx={{ mt: 2 }}
                                                    >
                                                        {selectedTransaction.docKind === 'deposit_slip' ? 'View deposit slip' : 'View check image'}
                                                    </Button>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Category Information - Takes full width if no flag */}
                                <Grid item xs={12} md={selectedTransaction.flagName ? 6 : 12}>
                                    <Card variant="outlined" sx={{ height: '100%' }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Categorization
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                                <Chip icon={<CategoryIcon />} label={selectedTransaction.categoryName} />
                                                <Chip variant="outlined" label={selectedTransaction.subcategoryName} />
                                            </Box>

                                            <Typography variant="subtitle2" color="text.secondary">
                                                Rationale
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedTransaction.categoryRationale}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Flag Information - Only show if a flag exists */}
                                {selectedTransaction.flagName && (
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined" sx={{ height: '100%', bgcolor: 'rgba(255, 0, 0, 0.03)' }}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom color="error">
                                                    Flag: {selectedTransaction.flagName}
                                                </Typography>
                                                <Box sx={{ mb: 2 }}>
                                                    <Chip
                                                        label={`Confidence: ${selectedTransaction.confidenceLevel}`}
                                                        size="small"
                                                        color={
                                                            selectedTransaction.confidenceLevel === 'VERY HIGH' ? 'error' :
                                                                selectedTransaction.confidenceLevel === 'HIGH' ? 'warning' : 'default'
                                                        }
                                                    />
                                                </Box>

                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Rationale
                                                </Typography>
                                                <Typography variant="body2">
                                                    {selectedTransaction.flagRationale}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Travel Information */}
                                {(selectedTransaction.tripOrigin || selectedTransaction.tripDestination ||
                                    (selectedTransaction.tripLocations && selectedTransaction.tripLocations.length > 0) ||
                                    selectedTransaction.tripStartDate || selectedTransaction.tripEndDate) && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        Travel Information
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        {selectedTransaction.tripOrigin && (
                                                            <Grid item xs={12} sm={6}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {/* Icon based on subcategory */}
                                                                    {selectedTransaction.subcategoryName === "Air Travel" ? (
                                                                        <FlightIcon color="primary" />
                                                                    ) : selectedTransaction.subcategoryName === "Hotel/Lodging" ? (
                                                                        <HotelIcon sx={{ color: '#E91E63' }} />
                                                                    ) : selectedTransaction.subcategoryName === "Rental Car" ? (
                                                                        <CarIcon sx={{ color: '#FF9800' }} />
                                                                    ) : (
                                                                        <PlaceIcon color="primary" />
                                                                    )}
                                                                    <div>
                                                                        <Typography variant="subtitle2" color="text.secondary">
                                                                            Origin
                                                                        </Typography>
                                                                        <Typography variant="body1">
                                                                            {selectedTransaction.tripOrigin}
                                                                        </Typography>
                                                                    </div>
                                                                </Box>
                                                            </Grid>
                                                        )}

                                                        {selectedTransaction.tripDestination && (
                                                            <Grid item xs={12} sm={6}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <PlaceIcon color="secondary" />
                                                                    <div>
                                                                        <Typography variant="subtitle2" color="text.secondary">
                                                                            Destination
                                                                        </Typography>
                                                                        <Typography variant="body1">
                                                                            {selectedTransaction.tripDestination}
                                                                        </Typography>
                                                                    </div>
                                                                </Box>
                                                            </Grid>
                                                        )}

                                                        {/* Trip Dates Section */}
                                                        {selectedTransaction.hasTripDates() && (
                                                            <Grid item xs={12} sm={selectedTransaction.tripStartDate && selectedTransaction.tripEndDate ? 12 : 6}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <EventIcon color="info" />
                                                                    <div>
                                                                        <Typography variant="subtitle2" color="text.secondary">
                                                                            {selectedTransaction.getTripDateLabel()}
                                                                        </Typography>
                                                                        <Typography variant="body1">
                                                                            {selectedTransaction.tripStartDate && formatDate(selectedTransaction.tripStartDate)}
                                                                            {selectedTransaction.tripStartDate && selectedTransaction.tripEndDate && ' to '}
                                                                            {selectedTransaction.tripEndDate && formatDate(selectedTransaction.tripEndDate)}
                                                                        </Typography>
                                                                    </div>
                                                                </Box>
                                                            </Grid>
                                                        )}

                                                        {selectedTransaction.tripLocations && selectedTransaction.tripLocations.length > 0 && (
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" color="text.secondary">
                                                                    Locations
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                                    {selectedTransaction.tripLocations.map((location, index) => (
                                                                        <Chip
                                                                            key={index}
                                                                            icon={<PlaceIcon />}
                                                                            label={location}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Grid>
                                                        )}

                                                        {/* Trip Origin/Destination Map - only when origin or destination has geocode data */}
                                                        {selectedTransaction.hasTripMapData() && (
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                    Trip Route Map
                                                                </Typography>
                                                                <TripRouteMap
                                                                    tripOrigin={selectedTransaction.tripOrigin}
                                                                    tripOriginGeocode={selectedTransaction.tripOriginGeocode}
                                                                    tripDestination={selectedTransaction.tripDestination}
                                                                    tripDestinationGeocode={selectedTransaction.tripDestinationGeocode}
                                                                    subcategory={selectedTransaction.subcategoryName}
                                                                />
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                {/* Associated Names */}
                                {selectedTransaction.associatedNames && selectedTransaction.associatedNames.length > 0 && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{ bgcolor: 'rgba(255, 0, 0, 0.03)' }}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom color="error">
                                                    Associated Names
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {selectedTransaction.associatedNames.map((name, index) => (
                                                        <Chip
                                                            key={index}
                                                            icon={<PersonIcon />}
                                                            label={name}
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            <ViewStatementDialog
                open={openViewStatementDialog}
                documentToView={selectedTransaction}
                highlightedTxn={selectedTransaction}
                onCancel={handleStatementPreviewClose}
            />
            <CheckImageLightbox
                open={openCheckImage}
                onClose={() => setOpenCheckImage(false)}
                txnID={selectedTransaction?.id}
                docKind={selectedTransaction?.docKind}
            />
        </Fragment>
    );
};

export default TransactionDetailDialog;